import { Analytics } from '@util/Analytics';
import { Auth, AuthError } from '@util/Auth';
import { recordError } from '@util/Errors';
import { UserFriendlyError, makeUserFriendlyError } from '@util/UserFriendlyError';
import { SurveyUpdatedBy } from 'hydra-data-types';
import { ActionType } from '../ActionTypes';
import { StoreAction } from '../Store';

export const DEFAULT_ERROR_MESSAGE = 'Something unexpected happened. Please try again.';

export function handleUserError(rawError: unknown): UserFriendlyError {
  const authError = Auth.isAuthError(rawError)
    ? new UserFriendlyError(authErrorMessage(rawError))
    : undefined;

  const error = makeUserFriendlyError(authError ?? rawError, DEFAULT_ERROR_MESSAGE);

  if (authError !== undefined) {
    Analytics.track({ event: Analytics.Event.a_auth_form_error, error });
  } else {
    recordError(error);
    Analytics.track({ event: Analytics.Event.a_unknown_auth_error, error });
  }

  return error;
}

function authErrorMessage(error: AuthError): string {
  switch (error.code) {
    // TODO: Derive from https://github.com/Mindset-Health/NervaRN/blob/ca9cbf8126467c1921079e399aa9b1d011f0b209/src/helpers/AuthApi.ts#L40
    case 'auth/no-fill-error':
    case 'auth/too-many-requests':
      return error.message;
    case 'auth/email-already-in-use':
    case 'auth/email-already-exists':
      return 'This email address is in use. Try Logging in instead.';
    case 'auth/invalid-email':
      return 'The email must be a valid email string.';
    case 'auth/invalid-password':
      return 'The password must be a string with at least 6 characters.';
    case 'auth/wrong-password': // This code is not returned if firebase email enumeration protection is enabled
      return 'Oops, you might have entered the wrong password.';
    case 'auth/user-not-found': // This code is not returned if firebase email enumeration protection is enabled
      return "This user doesn't exist yet. Try signing up instead.";
    case 'auth/network-request-failed':
      return 'Please check your internet connection and try again.';
    case 'auth/non-matching-email':
      return 'Emails do not match. Please make sure you have entered the same email in both fields.';
    case 'auth/invalid-credential':
      /* When firebase email enumeration protection is enabled, this error is returned for an
       * invalid email/password. When email enumeration protection is disabled, either 'auth/wrong-password'
       * or 'auth/user-not-found' are returned.
       * https://cloud.google.com/identity-platform/docs/admin/email-enumeration-protection
       * */
      return 'Invalid email or password. Please try again.';
    case 'auth/invalid-email-reset':
      return error.message;
    default:
      return DEFAULT_ERROR_MESSAGE;
  }
}

export function handleUnknownError(error: unknown): string {
  Analytics.track({ event: Analytics.Event.a_unknown_auth_error, error: error });
  recordError(error);
  return "Something didn't work as expected. Please try again in a moment.";
}

export function validateUserIsNotAuthenticated(): void | never {
  if (Auth.isUserSignedIn()) {
    throw new UserFriendlyError('You are already logged in with an account.');
  }
}

export function surveyCompletedAction(): StoreAction {
  return {
    type: ActionType.MERGE_USER_DATA,
    userData: {
      surveyUpdatedBy: SurveyUpdatedBy.Web,
      surveyUpdatedAt: Date.now(),
      /**
       * To "test the tests", we can replace
       *   surveyUpdatedAt: Date.now(),
       * with
       *   surveyUpdatedAt: Date.now() - 6 * 60 * 1000,
       * which will make tests checking the surveyUpdatedAt value is within the last 5 minutes fail.
       */
    },
  };
}
